import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  ArrowLeft, DollarSign, TrendingUp, Crown, Zap, Receipt, RefreshCw,
  Loader2, Download, Search, Calendar, AlertCircle, CheckCircle2,
  Building2, User, AlertTriangle, ChevronRight, BarChart3,
  Webhook, TrendingDown, Activity, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

const KPI = ({ icon: Icon, label, value, sub, accent = 'indigo', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg bg-${accent}-100 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${accent}-600`} />
        </div>
        {sub && <Badge className={`text-[10px] bg-${accent}-50 text-${accent}-700 border border-${accent}-200`}>{sub}</Badge>}
      </div>
      <p className="text-2xl font-bold text-gray-900 tracking-tight">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </CardContent>
  </Card>
);

// ============================================================================
// Iter-235f — Subscription Funnel diagnostic panel
// ============================================================================
//
// Sits between the "Active monetization" KPIs and the daily trend. Shows
// `started → completed → abandoned` for every subscription surface in
// the last N days, plus a role breakdown, sample abandoned-customer
// emails, and the canonical Stripe webhook URLs so the admin can
// verify them against the Stripe Dashboard. No redesign of the
// surrounding page — just a new section that complements the existing
// KPI grid.
const ROLE_META = {
  talent: { label: 'Talent Premium',  accent: 'amber',  Icon: Crown },
  client: { label: 'Client Plans',    accent: 'cyan',   Icon: Building2 },
  agency: { label: 'Agency Plans',    accent: 'violet', Icon: Building2 },
  other:  { label: 'Unclassified',    accent: 'gray',   Icon: AlertCircle },
};

const FunnelRoleCard = ({ roleKey, row }) => {
  const meta = ROLE_META[roleKey] || ROLE_META.other;
  const Icon = meta.Icon;
  return (
    <Card data-testid={`funnel-role-${roleKey}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-lg bg-${meta.accent}-100 flex items-center justify-center`}>
            <Icon className={`w-4 h-4 text-${meta.accent}-600`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{meta.label}</p>
            <p className="text-[10px] uppercase tracking-wide text-gray-400">Started → Done</p>
          </div>
          {row.abandonment_rate >= 80 && row.started > 0 && (
            <Badge className="ml-auto bg-rose-100 text-rose-700 text-[10px] border border-rose-200">
              <AlertTriangle className="w-3 h-3 mr-0.5" /> Leak
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-gray-900">{row.started}</p>
            <p className="text-[10px] text-gray-500 uppercase">Started</p>
          </div>
          <div>
            <p className="text-lg font-bold text-emerald-600">{row.completed}</p>
            <p className="text-[10px] text-gray-500 uppercase">Paid</p>
          </div>
          <div>
            <p className="text-lg font-bold text-rose-600">{row.abandoned}</p>
            <p className="text-[10px] text-gray-500 uppercase">Abandoned</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Abandonment</span>
            <span className={`font-semibold ${row.abandonment_rate >= 80 ? 'text-rose-600' : row.abandonment_rate >= 50 ? 'text-amber-600' : 'text-gray-700'}`}>
              {row.abandonment_rate}%
            </span>
          </div>
          {row.last_session_at && (
            <p className="text-[10px] text-gray-400 mt-1 truncate">
              Last attempt: {new Date(row.last_session_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SubscriptionFunnelPanel = ({ funnel, loading, days, onChangeDays, showDetail, onToggleDetail, onRefresh }) => {
  const totals = funnel?.totals || {};
  const byRole = funnel?.by_role || {};
  const activeByRole = funnel?.active_by_role || {};
  const webhookUrls = funnel?.webhook_urls || {};
  const copyToClipboard = (txt) => {
    navigator.clipboard?.writeText(txt).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed'),
    );
  };

  return (
    <section data-testid="subscription-funnel-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <Activity className="w-3 h-3" /> Subscription funnel · last {days} days
        </h2>
        <div className="flex items-center gap-2">
          <select
            className="text-xs border border-gray-200 rounded h-7 px-2 bg-white"
            value={days}
            onChange={(e) => onChangeDays(Number(e.target.value))}
            data-testid="funnel-days-select"
          >
            <option value={30}>30d</option>
            <option value={60}>60d</option>
            <option value={90}>90d</option>
            <option value={180}>180d</option>
            <option value={365}>365d</option>
          </select>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onRefresh} disabled={loading} data-testid="funnel-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading && !funnel ? (
        <Card><CardContent className="py-10 text-center text-xs text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />Loading checkout funnel from Stripe…</CardContent></Card>
      ) : !funnel ? null : (
        <>
          {funnel.warning && (
            <div className="mb-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2" data-testid="funnel-warning">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{funnel.warning}</span>
            </div>
          )}

          {/* Top-line funnel metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="funnel-kpis">
            <KPI icon={TrendingUp} label="Checkouts started" value={totals.started || 0} sub={`Stripe ${funnel.stripe_mode || '—'}`} accent="indigo" testId="funnel-kpi-started" />
            <KPI icon={CheckCircle2} label="Completed (paid)" value={totals.completed || 0} sub={totals.started > 0 ? `${((totals.completed / totals.started) * 100).toFixed(1)}% conv` : '—'} accent="emerald" testId="funnel-kpi-completed" />
            <KPI icon={TrendingDown} label="Abandoned" value={totals.abandoned || 0} sub={`${totals.abandonment_rate || 0}% drop-off`} accent="rose" testId="funnel-kpi-abandoned" />
            <KPI icon={Crown}
                 label="Active subscribers"
                 value={(activeByRole.talent || 0) + (activeByRole.client || 0) + (activeByRole.agency || 0)}
                 sub={`T:${activeByRole.talent || 0} · C:${activeByRole.client || 0} · A:${activeByRole.agency || 0}`}
                 accent="violet"
                 testId="funnel-kpi-active" />
          </div>

          {/* Per-role breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="funnel-role-grid">
            {['talent', 'client', 'agency', 'other'].map((k) => (
              <FunnelRoleCard key={k} roleKey={k} row={byRole[k] || { started: 0, completed: 0, abandoned: 0, abandonment_rate: 0 }} />
            ))}
          </div>

          {/* Collapsible: webhook URLs + sample abandoned customers */}
          <button
            type="button"
            onClick={onToggleDetail}
            className="mt-3 text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
            data-testid="funnel-toggle-detail"
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${showDetail ? 'rotate-180' : ''}`} />
            {showDetail ? 'Hide' : 'Show'} webhook URLs & sample abandoned customers
          </button>

          {showDetail && (
            <Card className="mt-2" data-testid="funnel-detail-card">
              <CardContent className="p-4 space-y-4">
                <div>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-2">
                    <Webhook className="w-3 h-3" /> Production webhook URLs (verify in Stripe Dashboard)
                  </h3>
                  <div className="space-y-1">
                    {Object.entries(webhookUrls).map(([k, url]) => (
                      <div key={k} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5" data-testid={`funnel-webhook-${k}`}>
                        <Badge className="bg-gray-200 text-gray-700 text-[10px] font-mono">{k}</Badge>
                        <code className="flex-1 truncate text-gray-700">{url}</code>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(url)}
                          className="text-indigo-600 hover:text-indigo-800 text-[10px] font-medium"
                          data-testid={`funnel-webhook-${k}-copy`}
                        >
                          Copy
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                    Each URL above must be registered in Stripe Dashboard → Developers → Webhooks
                    with signature verification enabled. The corresponding secret env vars are
                    <code className="ml-1">STRIPE_WEBHOOK_SECRET</code>,
                    <code className="ml-1">STRIPE_CLIENT_SUBSCRIPTION_WEBHOOK_SECRET</code>,
                    <code className="ml-1">STRIPE_TALENT_PREMIUM_WEBHOOK_SECRET</code>,
                    <code className="ml-1">STRIPE_AGENCY_WEBHOOK_SECRET</code>.
                  </p>
                </div>

                <div>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
                    Sample abandoned customers (for outreach)
                  </h3>
                  <div className="space-y-2">
                    {['talent', 'client', 'agency', 'other'].map((roleKey) => {
                      const r = byRole[roleKey] || {};
                      if (!r.sample_emails?.length) return null;
                      const meta = ROLE_META[roleKey];
                      return (
                        <div key={roleKey} className="text-xs" data-testid={`funnel-sample-${roleKey}`}>
                          <Badge className={`bg-${meta.accent}-100 text-${meta.accent}-700 text-[10px] mr-2`}>{meta.label}</Badge>
                          <span className="text-gray-700">{r.sample_emails.join(', ')}</span>
                        </div>
                      );
                    })}
                    {!Object.values(byRole).some((r) => r?.sample_emails?.length) && (
                      <p className="text-[11px] text-gray-400 italic">No abandoned customer emails captured by Stripe (anonymous checkouts).</p>
                    )}
                  </div>
                </div>

                <p className="text-[10px] text-gray-400">
                  Fetched <span className="font-medium">{funnel.stripe_fetch_count}</span> checkout sessions from Stripe ({funnel.stripe_mode}).
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </section>
  );
};

// ============================================================================
// Iter-235g — Stripe ↔ Database Reconciliation panel
// ============================================================================
//
// Sits directly below the Subscription Funnel. Pulls
// /api/admin/analytics/stripe-reconciliation and shows:
//   - 4 KPI cards: total charges, reconciled count, unreconciled count,
//     unreconciled $ amount
//   - A table of every Stripe charge that has NO matching internal
//     record (with email, amount, date, notes, and the Stripe charge id)
//
// This is the answer to "Identify the exact point where Stripe payment
// succeeds but the application state is not updated."

const STATUS_BADGES = {
  reconciled: { label: 'Reconciled', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  user_known_no_activation: { label: 'User exists · No activation', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  unknown_customer: { label: 'Unknown customer', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  unmatched: { label: 'Unmatched', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const StripeReconciliationPanel = ({ recon, loading, days, onChangeDays, showDetail, onToggleDetail, onRefresh }) => {
  const summary = recon?.summary || {};
  const rows = recon?.rows || [];
  const unreconciled = rows.filter((r) => r.internal?.activation_status !== 'reconciled');
  const [backfillingId, setBackfillingId] = useState(null);

  const copyJson = () => {
    if (!recon) return;
    navigator.clipboard?.writeText(JSON.stringify(recon, null, 2)).then(
      () => toast.success('Reconciliation JSON copied'),
      () => toast.error('Copy failed'),
    );
  };

  // Iter-235h — one-click retroactive activation for a paid-but-not-
  // activated charge. Server traverses Charge→PI→Session, reads
  // metadata.type, and dispatches to the right activation helper.
  const backfillCharge = async (chargeId) => {
    setBackfillingId(chargeId);
    try {
      const r = await api.post(`/admin/analytics/stripe-reconciliation/backfill/${chargeId}`);
      toast.success(`Activated ${r.data.type} for charge ${chargeId.slice(0, 16)}…`);
      onRefresh?.();
    } catch (err) {
      toast.error('Backfill failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setBackfillingId(null);
    }
  };

  // Iter-235i — bulk sweep
  const [bulkBackfilling, setBulkBackfilling] = useState(false);
  const backfillAll = async () => {
    if (!window.confirm('Sweep every paid-but-unactivated Stripe charge in this window and activate it? This is idempotent.')) return;
    setBulkBackfilling(true);
    try {
      const r = await api.post(`/admin/analytics/stripe-reconciliation/backfill-all?days=${days}`);
      const x = r.data;
      toast.success(`Bulk backfill: ${x.activated} activated · ${x.failed} failed · ${x.skipped_unknown_customer} unknown · ${x.skipped_no_session} no-session`);
      onRefresh?.();
    } catch (err) {
      toast.error('Bulk backfill failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setBulkBackfilling(false);
    }
  };

  return (
    <section data-testid="stripe-reconciliation-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <Activity className="w-3 h-3" /> Stripe ↔ Database reconciliation · last {days} days
        </h2>
        <div className="flex items-center gap-2">
          <select
            className="text-xs border border-gray-200 rounded h-7 px-2 bg-white"
            value={days}
            onChange={(e) => onChangeDays(Number(e.target.value))}
            data-testid="recon-days-select"
          >
            <option value={30}>30d</option>
            <option value={60}>60d</option>
            <option value={90}>90d</option>
            <option value={180}>180d</option>
            <option value={365}>365d</option>
          </select>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onRefresh} disabled={loading} data-testid="recon-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            onClick={backfillAll}
            disabled={bulkBackfilling || unreconciled.length === 0}
            data-testid="recon-backfill-all-btn"
          >
            {bulkBackfilling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
            Backfill all ({unreconciled.length})
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={copyJson} disabled={!recon} data-testid="recon-copy-json">
            <Download className="w-3 h-3 mr-1" /> JSON
          </Button>
        </div>
      </div>

      {loading && !recon ? (
        <Card><CardContent className="py-10 text-center text-xs text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />Pulling Stripe charges & matching against database…</CardContent></Card>
      ) : !recon ? null : (
        <>
          {recon.warning && (
            <div className="mb-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2" data-testid="recon-warning">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{recon.warning}</span>
            </div>
          )}

          {/* Top-line reconciliation KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="recon-kpis">
            <KPI icon={DollarSign} label="Stripe charges" value={summary.total_charges || 0} sub={`$${(summary.total_amount_usd || 0).toFixed(2)} gross`} accent="indigo" testId="recon-kpi-total" />
            <KPI icon={CheckCircle2} label="Reconciled" value={summary.reconciled || 0} sub={summary.total_charges > 0 ? `${((summary.reconciled / summary.total_charges) * 100).toFixed(0)}% matched` : '—'} accent="emerald" testId="recon-kpi-reconciled" />
            <KPI icon={AlertTriangle} label="No activation" value={(summary.user_known_no_activation || 0) + (summary.unknown_customer || 0)} sub={`${summary.user_known_no_activation || 0} known · ${summary.unknown_customer || 0} unknown`} accent="rose" testId="recon-kpi-unreconciled" />
            <KPI icon={TrendingDown} label="Unreconciled $" value={`$${(summary.total_unreconciled_amount_usd || 0).toFixed(2)}`} sub="Collected outside platform" accent="amber" testId="recon-kpi-unreconciled-amount" />
          </div>

          {summary.total_charges === 0 ? (
            <Card><CardContent className="py-6 text-center text-xs text-gray-400">No Stripe charges in this window.</CardContent></Card>
          ) : (
            <Card data-testid="recon-table-card">
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {unreconciled.length > 0
                      ? `${unreconciled.length} unreconciled charge${unreconciled.length === 1 ? '' : 's'}`
                      : 'All charges reconciled'}
                  </h3>
                  <button
                    type="button"
                    onClick={onToggleDetail}
                    className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                    data-testid="recon-toggle-detail"
                  >
                    <ChevronDown className={`w-3 h-3 transition-transform ${showDetail ? 'rotate-180' : ''}`} />
                    {showDetail ? 'Hide table' : 'Show table'}
                  </button>
                </div>
                {showDetail && (
                  <div className="overflow-x-auto" data-testid="recon-table">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Date</th>
                          <th className="px-3 py-2 text-left font-medium">Customer</th>
                          <th className="px-3 py-2 text-right font-medium">Amount</th>
                          <th className="px-3 py-2 text-left font-medium">Stripe charge</th>
                          <th className="px-3 py-2 text-left font-medium">Internal record</th>
                          <th className="px-3 py-2 text-left font-medium">Status</th>
                          <th className="px-3 py-2 text-left font-medium">Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => {
                          const st = row.internal?.activation_status || 'unmatched';
                          const badge = STATUS_BADGES[st] || STATUS_BADGES.unmatched;
                          return (
                            <tr key={row.stripe_charge_id} className="border-t hover:bg-gray-50/60" data-testid={`recon-row-${row.stripe_charge_id}`}>
                              <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{row.customer_name || '—'}</div>
                                <div className="text-[10px] text-gray-500">{row.customer_email || '—'}</div>
                              </td>
                              <td className="px-3 py-2 text-right font-medium tabular-nums">${row.amount_usd?.toFixed(2)}</td>
                              <td className="px-3 py-2">
                                <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">{row.stripe_charge_id?.slice(0, 16)}…</code>
                              </td>
                              <td className="px-3 py-2 text-[10px] text-gray-600">
                                {row.internal?.matched_tables?.length
                                  ? row.internal.matched_tables.join(', ')
                                  : <span className="italic text-gray-400">none</span>}
                              </td>
                              <td className="px-3 py-2">
                                <Badge className={`text-[10px] border ${badge.cls}`}>{badge.label}</Badge>
                                {st !== 'reconciled' && row.internal?.metadata_type && row.internal?.user_known && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="ml-2 h-6 text-[10px] px-2"
                                    disabled={backfillingId === row.stripe_charge_id}
                                    onClick={() => backfillCharge(row.stripe_charge_id)}
                                    data-testid={`recon-backfill-${row.stripe_charge_id}`}
                                  >
                                    {backfillingId === row.stripe_charge_id
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : 'Backfill'}
                                  </Button>
                                )}
                              </td>
                              <td className="px-3 py-2 text-[10px] text-gray-600 max-w-[280px]">
                                {(row.internal?.notes || []).map((n, i) => (
                                  <div key={i} className="leading-snug">• {n}</div>
                                ))}
                                {!row.internal?.notes?.length && <span className="text-gray-400 italic">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="px-4 py-2 border-t bg-gray-50 text-[10px] text-gray-500">
                  Pulled {summary.total_charges} charges from Stripe ({recon.stripe_mode}). Reconciled = matched to one or more of:
                  payment_transactions · talent_subscriptions · client_subscriptions · agency_subscriptions ·
                  talent_profiles.is_featured · shifts.is_featured.
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </section>
  );
};

const AdminRevenue = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelDays, setFunnelDays] = useState(90);
  const [showFunnelDetail, setShowFunnelDetail] = useState(false);
  // Iter-235g — Stripe ↔ DB reconciliation
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconDays, setReconDays] = useState(180);
  const [showReconDetail, setShowReconDetail] = useState(true);
  const [search, setSearch] = useState('');

  const load = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const [revRes, snapRes] = await Promise.all([
        api.get('/admin/analytics/revenue'),
        api.get('/analytics/revenue/snapshot').catch(() => ({ data: null })),
      ]);
      setData(revRes.data);
      setSnapshot(snapRes.data);
    } catch (err) {
      toast.error('Failed to load revenue data', { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // Iter-235f — kick the funnel fetch in the background so it doesn't
    // block first paint of the main dashboard.
    loadFunnel(funnelDays);
    // Iter-235g — same for the Stripe reconciliation report.
    loadRecon(reconDays);
  };

  // Iter-235f — Stripe-backed subscription funnel diagnostics. The
  // initial Stripe call can take 1-3s so we kick it off from the main
  // `load` flow but defer the `setFunnelLoading(true)` to the next tick
  // to keep React 19's "no setState in effect" lint rule happy.
  const loadFunnel = (days = funnelDays) => {
    setTimeout(async () => {
      setFunnelLoading(true);
      try {
        const r = await api.get(`/admin/analytics/subscription-funnel?days=${days}`);
        setFunnel(r.data);
      } catch (err) {
        toast.error('Failed to load subscription funnel', { description: err?.response?.data?.detail || err.message });
      } finally {
        setFunnelLoading(false);
      }
    }, 0);
  };

  // Iter-235g — Stripe ↔ DB reconciliation report. Surfaces every
  // succeeded Stripe charge in the lookback window that has no
  // matching internal record (subscription / boost / payment_transaction).
  const loadRecon = (days = reconDays) => {
    setTimeout(async () => {
      setReconLoading(true);
      try {
        const r = await api.get(`/admin/analytics/stripe-reconciliation?days=${days}`);
        setRecon(r.data);
      } catch (err) {
        toast.error('Failed to load Stripe reconciliation', { description: err?.response?.data?.detail || err.message });
      } finally {
        setReconLoading(false);
      }
    }, 0);
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const summary = data?.summary || {};
  const breakdown = data?.breakdown || {};
  const trend = data?.trend || [];
  const subs = data?.active_subscriptions || [];
  const boosts = data?.active_boosts || [];
  const payments = data?.recent_payments || [];

  const filteredPayments = useMemo(() => {
    if (!search.trim()) return payments;
    const q = search.toLowerCase();
    return payments.filter(p =>
      (p.user_email || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.type || '').toLowerCase().includes(q)
    );
  }, [payments, search]);

  const maxTrend = Math.max(1, ...trend.map(t => t.amount || 0));

  const exportCSV = () => {
    const lines = [
      ['Date', 'Source', 'Type', 'User', 'Description', 'Amount (USD)'].join(','),
      ...payments.map(p => [
        p.at || '',
        p.source || '',
        p.type || '',
        p.user_email || '',
        `"${(p.description || '').replace(/"/g, '""')}"`,
        p.amount,
      ].join(',')),
    ];
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_payments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="revenue-loading">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-revenue-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="back-to-admin-home">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900">Revenue</h1>
              <p className="text-xs text-gray-500 hidden sm:block">Subscriptions, boosts, paid invoices — unified</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing} data-testid="refresh-revenue-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-revenue-csv">
              <Download className="w-4 h-4 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Receivables & Collections — relocated from Admin Dashboard (Iter-133n) */}
        {snapshot && (
          <section data-testid="receivables-section">
            <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Receivables & collections</h2>

            {/* Collected cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-3">
              <button type="button" onClick={() => navigate('/admin/invoices?filter=paid')} className="text-left" data-testid="rev-card-week">
                <Card className="bg-gradient-to-br from-[#03DFFC]/5 to-white border-[#03DFFC]/20 shadow-sm hover:shadow-md hover:border-[#03DFFC]/40 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Collected · Week</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">${snapshot.weekly?.total?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{snapshot.weekly?.count || 0} paid invoices</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-[#03DFFC]/10 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-[#03DFFC]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
              <button type="button" onClick={() => navigate('/admin/invoices?filter=paid')} className="text-left" data-testid="rev-card-mtd">
                <Card className="bg-gradient-to-br from-[#FF2D95]/5 to-white border-[#FF2D95]/20 shadow-sm hover:shadow-md hover:border-[#FF2D95]/40 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Collected · MTD</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">${snapshot.month_to_date?.total?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{snapshot.month_to_date?.count || 0} paid invoices</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-[#FF2D95]/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-[#FF2D95]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
              <button type="button" onClick={() => navigate('/admin/invoices?filter=paid')} className="text-left" data-testid="rev-card-ytd">
                <Card className="bg-gradient-to-br from-green-500/5 to-white border-green-200/50 shadow-sm hover:shadow-md hover:border-green-400/60 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Collected · YTD</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">${snapshot.year_to_date?.total?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{snapshot.year_to_date?.count || 0} paid invoices</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            </div>

            {/* Receivables row — open / overdue / upcoming */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-3">
              <button type="button" onClick={() => navigate('/admin/invoices?filter=unpaid')} className="text-left" data-testid="rev-card-open">
                <Card className="bg-white border-sky-200 hover:shadow-md hover:border-sky-400 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Open Receivables</p>
                    <p className="text-2xl font-bold text-sky-700 mt-1">${(snapshot.open_receivables?.total || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{snapshot.open_receivables?.count || 0} invoices outstanding</p>
                  </CardContent>
                </Card>
              </button>
              <button type="button" onClick={() => navigate('/admin/invoices?filter=overdue')} className="text-left" data-testid="rev-card-overdue">
                <Card className="bg-white border-red-200 hover:shadow-md hover:border-red-400 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Overdue</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">${(snapshot.overdue?.total || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{snapshot.overdue?.count || 0} past due</p>
                  </CardContent>
                </Card>
              </button>
              <button type="button" onClick={() => navigate('/admin/invoices?filter=unpaid')} className="text-left" data-testid="rev-card-upcoming">
                <Card className="bg-white border-amber-200 hover:shadow-md hover:border-amber-400 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Upcoming · 30d</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1">${(snapshot.upcoming_30_days || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">${(snapshot.upcoming_7_days || 0).toFixed(2)} in next 7d</p>
                  </CardContent>
                </Card>
              </button>
            </div>

            {/* Aging Receivables */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3" data-testid="aging-receivables">
              {[
                { key: 'current',  label: 'Current',   tone: 'sky',    url: '/admin/invoices?filter=unpaid' },
                { key: 'd0_30',    label: '0–30 days', tone: 'amber',  url: '/admin/invoices?filter=overdue&bucket=d0_30' },
                { key: 'd31_60',   label: '31–60',     tone: 'orange', url: '/admin/invoices?filter=overdue&bucket=d31_60' },
                { key: 'd61_90',   label: '61–90',     tone: 'red',    url: '/admin/invoices?filter=overdue&bucket=d61_90' },
                { key: 'd90_plus', label: '90+ days',  tone: 'rose',   url: '/admin/invoices?filter=overdue&bucket=d90_plus' },
              ].map((b) => {
                const bucket = snapshot.aging?.[b.key] || { total: 0, count: 0 };
                const toneMap = {
                  sky:    'border-sky-200 hover:border-sky-400 text-sky-700',
                  amber:  'border-amber-200 hover:border-amber-400 text-amber-700',
                  orange: 'border-orange-300 hover:border-orange-500 text-orange-700',
                  red:    'border-red-300 hover:border-red-500 text-red-600',
                  rose:   'border-rose-400 hover:border-rose-600 text-rose-700 bg-rose-50/30',
                };
                return (
                  <button key={b.key} type="button" onClick={() => navigate(b.url)} className="text-left" data-testid={`aging-${b.key}`}>
                    <Card className={`bg-white ${toneMap[b.tone]} hover:shadow-md transition-all cursor-pointer`}>
                      <CardContent className="p-4">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{b.label}</p>
                        <p className="text-xl font-bold mt-1">${(bucket.total || 0).toFixed(2)}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{bucket.count || 0} invoice{(bucket.count || 0) === 1 ? '' : 's'}</p>
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
            </div>

            {(snapshot.stale?.count || 0) > 0 && (
              <button type="button" onClick={() => navigate('/admin/invoices?filter=overdue&stale=1')} className="w-full text-left" data-testid="stale-invoices-card">
                <Card className="bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300 hover:border-amber-500 transition-all cursor-pointer">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-yellow-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-yellow-900">
                        {snapshot.stale.count} stale invoice{snapshot.stale.count === 1 ? '' : 's'} · ${snapshot.stale.total.toFixed(2)} at risk
                      </p>
                      <p className="text-xs text-yellow-800 mt-0.5">
                        Overdue with no reminder activity — these are leaking. Click to chase.
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-yellow-700" />
                  </CardContent>
                </Card>
              </button>
            )}
          </section>
        )}

        {/* KPI row */}
        <section>
          <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="revenue-kpis">
            <KPI icon={DollarSign} label="All-time revenue" value={fmtMoney(summary.total_revenue_all_time)} accent="emerald" testId="kpi-total-revenue" />
            <KPI icon={TrendingUp} label="Last 30 days" value={fmtMoney(summary.revenue_30d)} sub={`7d: ${fmtMoney(summary.revenue_7d)}`} accent="indigo" testId="kpi-revenue-30d" />
            <KPI icon={RefreshCw} label="Estimated MRR" value={fmtMoney(summary.mrr_estimate)} sub={`${summary.active_talent_subscriptions + summary.active_client_subscriptions} subs`} accent="violet" testId="kpi-mrr" />
            <KPI icon={AlertCircle} label="Outstanding invoices" value={fmtMoney(summary.outstanding_invoices_total)} sub={`${summary.outstanding_invoices_count} open`} accent="amber" testId="kpi-outstanding" />
          </div>
        </section>

        {/* Secondary KPIs */}
        <section>
          <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Active monetization</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="active-kpis">
            <KPI icon={Crown} label="Talent premium subs" value={summary.active_talent_subscriptions} sub={fmtMoney(breakdown.talent_premium_mrr) + ' / mo'} accent="amber" testId="kpi-talent-subs" />
            <KPI icon={Building2} label="Client plan subs" value={summary.active_client_subscriptions} sub={fmtMoney(breakdown.client_subscriptions_mrr) + ' / mo'} accent="cyan" testId="kpi-client-subs" />
            <KPI icon={Zap} label="Active talent boosts" value={summary.active_talent_boosts} accent="pink" testId="kpi-talent-boosts" />
            <KPI icon={Receipt} label="Active gig boosts" value={summary.active_gig_boosts} accent="teal" testId="kpi-gig-boosts" />
          </div>
        </section>

        {/* Iter-235f — Subscription Funnel Diagnostic */}
        <SubscriptionFunnelPanel
          funnel={funnel}
          loading={funnelLoading}
          days={funnelDays}
          onChangeDays={(d) => { setFunnelDays(d); loadFunnel(d); }}
          showDetail={showFunnelDetail}
          onToggleDetail={() => setShowFunnelDetail((v) => !v)}
          onRefresh={() => loadFunnel(funnelDays)}
        />

        {/* Iter-235g — Stripe ↔ DB Reconciliation */}
        <StripeReconciliationPanel
          recon={recon}
          loading={reconLoading}
          days={reconDays}
          onChangeDays={(d) => { setReconDays(d); loadRecon(d); }}
          showDetail={showReconDetail}
          onToggleDetail={() => setShowReconDetail((v) => !v)}
          onRefresh={() => loadRecon(reconDays)}
        />

        {/* Trend */}
        <Card data-testid="revenue-trend">
          <CardHeader className="py-3 px-5 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Daily revenue — last 60 days</CardTitle>
              <span className="text-xs text-gray-500">Paid invoices + one-time Stripe charges</span>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            {trend.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400" data-testid="trend-empty">No paid revenue recorded yet.</div>
            ) : (
              <div>
                <div className="h-28 flex items-end gap-px" role="img" aria-label="Daily revenue trend">
                  {trend.map((t, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gradient-to-t from-emerald-400 to-emerald-300 rounded-t relative group min-w-[3px]"
                      style={{ height: `${Math.max(3, (t.amount / maxTrend) * 100)}%` }}
                      title={`${t.date}: ${fmtMoney(t.amount)}`}
                    >
                      <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                        {t.date}: {fmtMoney(t.amount)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500">
                  <span>{trend[0]?.date}</span>
                  <span>Peak: {fmtMoney(maxTrend)}</span>
                  <span>{trend[trend.length - 1]?.date}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="payments" className="w-full">
          <TabsList className="bg-white border border-gray-200 p-1 rounded-lg">
            <TabsTrigger value="payments" data-testid="tab-payments">Recent payments</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions ({subs.length})</TabsTrigger>
            <TabsTrigger value="boosts" data-testid="tab-boosts">Active boosts ({boosts.length})</TabsTrigger>
            <TabsTrigger value="breakdown" data-testid="tab-breakdown">Breakdown</TabsTrigger>
          </TabsList>

          {/* Recent payments */}
          <TabsContent value="payments" className="mt-3">
            <Card>
              <CardHeader className="py-3 px-5 border-b flex flex-row items-center justify-between gap-3">
                <CardTitle className="text-sm font-semibold">Latest 20 payments</CardTitle>
                <div className="relative w-full max-w-xs">
                  <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                  <Input
                    className="pl-8 h-9 text-sm"
                    placeholder="Filter by user, type…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="payments-search"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredPayments.length === 0 ? (
                  <div className="py-12 text-center text-sm text-gray-400" data-testid="payments-empty">
                    {search ? 'No matches.' : 'No payments yet.'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredPayments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`payment-row-${i}`}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${p.source === 'invoice' ? 'bg-emerald-100' : 'bg-indigo-100'}`}>
                            {p.source === 'invoice' ? <Receipt className="w-4 h-4 text-emerald-600" /> : <Zap className="w-4 h-4 text-indigo-600" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{p.description}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {p.user_email || 'unknown user'} · {fmtDate(p.at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-emerald-700">{fmtMoney(p.amount)}</p>
                          <Badge className="text-[9px] bg-gray-100 text-gray-600 font-normal mt-0.5">{p.type}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Active subscriptions */}
          <TabsContent value="subscriptions" className="mt-3">
            <Card>
              <CardHeader className="py-3 px-5 border-b">
                <CardTitle className="text-sm font-semibold">Active subscriptions</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {subs.length === 0 ? (
                  <div className="py-12 text-center text-sm text-gray-400" data-testid="subs-empty">
                    <Crown className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                    No active subscriptions yet.
                  </div>
                ) : (
                  <div className="divide-y">
                    {subs.map((s, i) => {
                      const renewsIn = daysUntil(s.renews_at);
                      return (
                        <div key={i} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`sub-row-${i}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.kind === 'talent_premium' ? 'bg-amber-100' : 'bg-cyan-100'}`}>
                              {s.kind === 'talent_premium' ? <Crown className="w-4 h-4 text-amber-600" /> : <Building2 className="w-4 h-4 text-cyan-600" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {s.email} · {s.kind === 'talent_premium' ? `Tier ${s.tier || '—'}` : `Plan ${s.plan_id || '—'}`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-gray-900">{fmtMoney(s.monthly_price)}<span className="text-xs font-normal text-gray-500"> / mo</span></p>
                            {renewsIn !== null && (
                              <p className="text-[11px] text-gray-500 flex items-center gap-1 justify-end">
                                <Calendar className="w-3 h-3" /> renews in {renewsIn}d
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Active boosts */}
          <TabsContent value="boosts" className="mt-3">
            <Card>
              <CardHeader className="py-3 px-5 border-b">
                <CardTitle className="text-sm font-semibold">Active boosts</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {boosts.length === 0 ? (
                  <div className="py-12 text-center text-sm text-gray-400" data-testid="boosts-empty">
                    <Zap className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                    No active boosts right now.
                  </div>
                ) : (
                  <div className="divide-y">
                    {boosts.map((b, i) => {
                      const expIn = daysUntil(b.expires_at);
                      return (
                        <div key={i} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`boost-row-${i}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${b.kind === 'talent_visibility' ? 'bg-pink-100' : 'bg-teal-100'}`}>
                              {b.kind === 'talent_visibility' ? <User className="w-4 h-4 text-pink-600" /> : <Building2 className="w-4 h-4 text-teal-600" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{b.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {b.email}
                                {b.venue_name ? ` · ${b.venue_name}` : ''}
                                {b.boost_type ? ` · ${b.boost_type}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <Badge className="bg-green-100 text-green-700 border border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>
                            {expIn !== null && (
                              <p className="text-[11px] text-gray-500 mt-1">expires in {expIn}d</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Breakdown */}
          <TabsContent value="breakdown" className="mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card data-testid="breakdown-invoices">
                <CardHeader className="py-3 px-5 border-b">
                  <CardTitle className="text-sm font-semibold">Paid invoices (all-time)</CardTitle>
                </CardHeader>
                <CardContent className="p-5">
                  <p className="text-3xl font-bold text-emerald-700">{fmtMoney(breakdown.invoices_paid?.total)}</p>
                  <p className="text-xs text-gray-500 mt-1">{breakdown.invoices_paid?.count || 0} invoices paid</p>
                </CardContent>
              </Card>
              <Card data-testid="breakdown-mrr">
                <CardHeader className="py-3 px-5 border-b">
                  <CardTitle className="text-sm font-semibold">Recurring MRR</CardTitle>
                </CardHeader>
                <CardContent className="p-5 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Talent premium</span>
                    <span className="font-semibold text-amber-700">{fmtMoney(breakdown.talent_premium_mrr)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Client plans</span>
                    <span className="font-semibold text-cyan-700">{fmtMoney(breakdown.client_subscriptions_mrr)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="text-sm font-medium text-gray-900">Total</span>
                    <span className="font-bold text-violet-700">{fmtMoney((breakdown.talent_premium_mrr || 0) + (breakdown.client_subscriptions_mrr || 0))}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="md:col-span-2" data-testid="breakdown-stripe-types">
                <CardHeader className="py-3 px-5 border-b">
                  <CardTitle className="text-sm font-semibold">Stripe one-time charges by type</CardTitle>
                </CardHeader>
                <CardContent className="p-5">
                  {Object.keys(breakdown.stripe_by_type || {}).length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No one-time Stripe charges recorded.</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(breakdown.stripe_by_type || {}).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between border rounded p-3" data-testid={`stripe-type-${k}`}>
                          <div>
                            <p className="text-sm font-medium text-gray-900 capitalize">{k.replace(/_/g, ' ')}</p>
                            <p className="text-[11px] text-gray-500">{v.count} charge{v.count === 1 ? '' : 's'}</p>
                          </div>
                          <p className="text-sm font-bold text-gray-900">{fmtMoney(v.total)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminRevenue;
